import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as favouritesApi from '../api/endpoints/favourites';
import { useAuth } from './AuthContext';
import { useOptionalDeliveryLocation } from './DeliveryLocationContext';
import { isStaleLocationError } from '../utils/staleLocation';

const FavouritesContext = createContext(null);
export const FAVOURITE_AUTH_REQUIRED = Object.freeze({ status: 'auth-required' });

// Favourites are server-backed (Customer.favouriteProductIds), unlike the
// cart which is guest-friendly localStorage — so this context only loads
// and mutates state once a customer is actually authenticated, and stays
// an empty set for guests without ever calling the (auth-required) API.
export const FavouritesProvider = ({ children }) => {
  const { authStatus } = useAuth();
  // `products` are projected for the delivery location (price, stock, outlet
  // opt-outs), so they are reloaded when it changes. Optional so the provider
  // still works standalone.
  const locationVersion = useOptionalDeliveryLocation()?.locationVersion ?? 0;
  const locationVersionRef = useRef(locationVersion);
  const [ids, setIds] = useState(() => new Set());
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // Customer profile hydration happens after authentication and must not reset
  // in-flight writes. Login/logout transitions are the actual session boundary.
  const authScopeKey = authStatus;
  const authScopeRef = useRef({ key: authScopeKey, generation: 0 });
  const loadRequestIdRef = useRef(0);
  const mutationVersionRef = useRef(0);
  const productMutationIdsRef = useRef(new Map());
  const productMutationQueuesRef = useRef(new Map());
  const desiredStateRef = useRef(new Map());
  const confirmedIdsRef = useRef(new Set());
  const idsRef = useRef(ids);
  const sessionInvalidatedRef = useRef(false);

  idsRef.current = ids;

  // Update during render so a response from the previous identity is stale as
  // soon as auth changes, before passive effect cleanup has a chance to run.
  if (authScopeRef.current.key !== authScopeKey) {
    authScopeRef.current = {
      key: authScopeKey,
      generation: authScopeRef.current.generation + 1,
    };
  }

  const loadFavourites = useCallback(() => {
    if (authStatus !== 'authenticated') return Promise.resolve(FAVOURITE_AUTH_REQUIRED);

    setLoaded(false);
    setLoadError(null);
    const requestId = ++loadRequestIdRef.current;
    const authGeneration = authScopeRef.current.generation;
    const requestedScopeKey = authScopeKey;
    const mutationVersion = mutationVersionRef.current;

    return favouritesApi
      .listFavourites()
      .then((response) => {
        if (
          requestedScopeKey !== authScopeRef.current.key ||
          authGeneration !== authScopeRef.current.generation ||
          requestId !== loadRequestIdRef.current ||
          mutationVersion !== mutationVersionRef.current
        ) {
          return { status: 'stale' };
        }
        const nextIds = new Set(response.data?.ids || []);
        const nextProducts = Array.isArray(response.data?.products) ? response.data.products : [];
        confirmedIdsRef.current = new Set(nextIds);
        desiredStateRef.current.clear();
        setIds(nextIds);
        setProducts(nextProducts);
        setLoaded(true);
        return { status: 'loaded' };
      })
      .catch((error) => {
        if (
          requestedScopeKey !== authScopeRef.current.key ||
          authGeneration !== authScopeRef.current.generation ||
          requestId !== loadRequestIdRef.current ||
          mutationVersion !== mutationVersionRef.current ||
          isStaleLocationError(error)
        ) {
          return { status: 'stale' };
        }
        setLoadError(error);
        setLoaded(true);
        return { status: 'error', error };
      });
  }, [authStatus, authScopeKey]);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      sessionInvalidatedRef.current = false;
      confirmedIdsRef.current = new Set();
      desiredStateRef.current.clear();
      productMutationQueuesRef.current.clear();
      setIds(new Set());
      setProducts([]);
      setLoadError(null);
      setLoaded(false);
      loadFavourites();
    } else {
      ++loadRequestIdRef.current;
      confirmedIdsRef.current = new Set();
      desiredStateRef.current.clear();
      productMutationQueuesRef.current.clear();
      setIds(new Set());
      setProducts([]);
      setLoadError(null);
      setLoaded(authStatus !== 'loading');
    }
  }, [authStatus, authScopeKey, loadFavourites]);

  // A new delivery geography re-projects the same favourites. Membership is
  // deliberately left as it is while reloading — the backend returns every id
  // regardless of location, so hearts never flicker off — and the newer
  // request id makes any in-flight response for the old location stale.
  useEffect(() => {
    if (locationVersionRef.current === locationVersion) return;
    locationVersionRef.current = locationVersion;
    if (authStatus === 'authenticated') loadFavourites();
  }, [locationVersion, authStatus, loadFavourites]);

  const isFavourite = useCallback((productId) => ids.has(String(productId)), [ids]);

  const updateFavourite = useCallback(
    (productId, shouldBeFavourite, product = null) => {
      if (authStatus !== 'authenticated' || sessionInvalidatedRef.current) {
        return Promise.resolve(FAVOURITE_AUTH_REQUIRED);
      }

      const key = String(productId);
      const authGeneration = authScopeRef.current.generation;
      const mutationId = (productMutationIdsRef.current.get(key) || 0) + 1;
      productMutationIdsRef.current.set(key, mutationId);
      desiredStateRef.current.set(key, shouldBeFavourite);
      ++mutationVersionRef.current;

      // Starting a mutation invalidates older list responses. The explicit
      // target state also lets removal always use DELETE, even before a list loads.
      setIds((prev) => {
        const next = new Set(prev);
        if (shouldBeFavourite) next.add(key);
        else next.delete(key);
        return next;
      });
      if (shouldBeFavourite && product) {
        setProducts((prev) => (
          prev.some((item) => String(item._id) === key) ? prev : [...prev, product]
        ));
      }
      setLoaded(true);

      // Serialize writes for each product. Without this queue, a fast add then
      // remove can reach the server in reverse order and leave it out of sync
      // with the optimistic heart state shown in the UI.
      const previousMutation = productMutationQueuesRef.current.get(key) || Promise.resolve();
      const mutation = previousMutation
        .catch(() => {
          // A later desired state should still be attempted after an earlier
          // non-auth failure; its result will reconcile the confirmed state.
        })
        .then(async () => {
          if (
            sessionInvalidatedRef.current ||
            authGeneration !== authScopeRef.current.generation
          ) {
            if (
              authGeneration === authScopeRef.current.generation &&
              productMutationIdsRef.current.get(key) === mutationId
            ) {
              const confirmedFavourite = confirmedIdsRef.current.has(key);
              setIds((prev) => {
                const next = new Set(prev);
                if (confirmedFavourite) next.add(key);
                else next.delete(key);
                return next;
              });
            }
            return FAVOURITE_AUTH_REQUIRED;
          }

          try {
            if (shouldBeFavourite) await favouritesApi.addFavourite(key);
            else await favouritesApi.removeFavourite(key);

            if (authGeneration === authScopeRef.current.generation) {
              const nextConfirmed = new Set(confirmedIdsRef.current);
              if (shouldBeFavourite) nextConfirmed.add(key);
              else nextConfirmed.delete(key);
              confirmedIdsRef.current = nextConfirmed;
            }

            return { status: 'updated', isFavourite: shouldBeFavourite };
          } catch (err) {
            const authRequired = err?.code === 'SESSION_EXPIRED' || err?.status === 401;
            if (authRequired) sessionInvalidatedRef.current = true;

            if (
              authGeneration === authScopeRef.current.generation &&
              productMutationIdsRef.current.get(key) === mutationId
            ) {
              const confirmedFavourite = confirmedIdsRef.current.has(key);
              setIds((prev) => {
                const next = new Set(prev);
                if (confirmedFavourite) next.add(key);
                else next.delete(key);
                return next;
              });
            }

            if (authRequired) return FAVOURITE_AUTH_REQUIRED;
            throw err;
          }
        })
        .finally(() => {
          if (productMutationQueuesRef.current.get(key) === mutation) {
            productMutationQueuesRef.current.delete(key);
          }
          if (productMutationIdsRef.current.get(key) === mutationId) {
            desiredStateRef.current.delete(key);
          }
        });

      productMutationQueuesRef.current.set(key, mutation);
      return mutation;
    },
    [authStatus]
  );

  const toggleFavourite = useCallback(
    (productId, product = null) => {
      const key = String(productId);
      const currentDesiredState = desiredStateRef.current.has(key)
        ? desiredStateRef.current.get(key)
        : idsRef.current.has(key);
      return updateFavourite(key, !currentDesiredState, product);
    },
    [updateFavourite]
  );
  const addFavourite = useCallback(
    (productId, product = null) => updateFavourite(productId, true, product),
    [updateFavourite]
  );
  const removeFavourite = useCallback((productId) => updateFavourite(productId, false), [updateFavourite]);

  const value = useMemo(
    () => ({
      isFavourite,
      toggleFavourite,
      addFavourite,
      removeFavourite,
      loaded,
      loadError,
      favouriteProducts: products,
      refreshFavourites: loadFavourites,
      favouriteCount: ids.size,
    }),
    [isFavourite, toggleFavourite, addFavourite, removeFavourite, loaded, loadError, products, loadFavourites, ids.size]
  );

  return <FavouritesContext.Provider value={value}>{children}</FavouritesContext.Provider>;
};

export const useFavourites = () => {
  const ctx = useContext(FavouritesContext);
  if (!ctx) throw new Error('useFavourites must be used within a FavouritesProvider');
  return ctx;
};
